import { LayoutType } from "@kalyzee/escaet-ui";
import { MeetingContainer, MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { Meeting } from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useMultiMeetingSelector } from "../../../hooks/meeting";
import { ConfirmableEscaetPedagogyAction, EscaetPedagogyAction } from "../../../interfaces/pedagogy";
import { ExpertContextState, useExpertContextState } from "../expert/context";

export interface PedagogyContextState extends ExpertContextState {
  containers: MeetingContainer[];
  setContainers: (containers: MeetingContainer[]) => void;
  pedagogyContainers: MeetingContainer[];
  setPedagogyContainers: (containers: MeetingContainer[]) => void;
  containersExceptPedagogy: MeetingContainer[];

  actions: EscaetPedagogyAction[];
  setActions: (actions: EscaetPedagogyAction[]) => void;

  confirmableAction: ConfirmableEscaetPedagogyAction | undefined;
  setConfirmableAction: (action: ConfirmableEscaetPedagogyAction | undefined) => void;

  pedagogyMeetings: Meeting[];

  disabledExpertMediaSessionIds: string[];
  setDisabledExpertMediaSessionIds: (ids: string[]) => void;
  enableExpertMediaSessionId: (id: string | string[]) => void;
  disableExpertMediaSessionId: (id: string | string[]) => void;

  scheduleDestroyContainer: (containers: MeetingContainer[], delay?: number) => void;
  unscheduleDestroyContainer: (containers: MeetingContainer[]) => void;

  audioValidator: (session: MeetingSessionOvenMediaReceiver) => boolean;
  addAudioValidator: (validator: (session: MeetingSessionOvenMediaReceiver) => boolean) => { flush: () => void };
  removeAudioValidator: (validator: (session: MeetingSessionOvenMediaReceiver) => boolean) => void;

  layout: LayoutType;
  setLayout: (layout: LayoutType) => void;

  displayRemoteController: boolean;
  setDisplayRemoteController: (value: boolean) => void;
}

const PedagogyContext = React.createContext<PedagogyContextState | undefined>(undefined);

export interface PedagogyContextProviderProps {
  children: React.ReactNode;
}

export const usePedagogyContext = (): PedagogyContextState | undefined => useContext(PedagogyContext);

export const PedagogyContextProvider = ({ children }: PedagogyContextProviderProps) => {
  const expertState = useExpertContextState();
  const [containers, setContainers] = useState<MeetingContainer[]>([]);
  const [pedagogyContainers, setPedagogyContainers] = useState<MeetingContainer[]>([]);
  const pedagogyMeetingAPIs = useMemo(() => pedagogyContainers.map((c) => c.getAPI()), [pedagogyContainers]);
  const [pedagogyMeetings] = useMultiMeetingSelector(pedagogyMeetingAPIs, (state) => state.meeting);
  const [disabledExpertMediaSessionIds, setDisabledExpertMediaSessionIds] = useState<string[]>([]);
  const containersToDestroyRef = useRef<{ container: MeetingContainer; timeout: NodeJS.Timeout }[]>([]);
  const audioValidatorsRef = useRef<((session: MeetingSessionOvenMediaReceiver) => boolean)[]>([]);
  const [audioValidatorCallback, setAudioValidatorCallback] = useState<{ callback: (session: MeetingSessionOvenMediaReceiver) => boolean }>({
    callback: () => false,
  });
  const [layout, setLayout] = useState(LayoutType.Square);
  const [displayRemoteController, setDisplayRemoteController] = useState(true);

  const [actions, setActions] = useState<EscaetPedagogyAction[]>([]);
  const [finalizableAction, setFinalizableAction] = useState<ConfirmableEscaetPedagogyAction>();

  const scheduleDestroyContainer = useCallback((containers: MeetingContainer[], delay = 1000) => {
    if (!containers) return;
    containersToDestroyRef.current.push(
      ...containers.map((c) => ({
        container: c,
        timeout: setTimeout(() => {
          c.destroy();
        }, delay),
      }))
    );
  }, []);

  const unscheduleDestroyContainer = useCallback((containers: MeetingContainer[]) => {
    if (!containers) return;
    containersToDestroyRef.current = containersToDestroyRef.current.filter((curr) => {
      if (containers.find((c) => curr.container === c)) {
        clearTimeout(curr.timeout);
        return false;
      }
      return true;
    });
  }, []);

  const updateAudioValidators = useCallback(() => {
    const validators = audioValidatorsRef.current;
    setAudioValidatorCallback({
      callback: (s) => {
        let result = true;
        for (let validator of validators) {
          if (!validator(s)) {
            result = false;
            break;
          }
        }
        return result;
      },
    });
  }, []);

  const removeAudioValidator = useCallback(
    (validator: (session: MeetingSessionOvenMediaReceiver) => boolean) => {
      audioValidatorsRef.current = audioValidatorsRef.current.filter((v) => v !== validator);
      updateAudioValidators();
    },
    [updateAudioValidators]
  );

  const addAudioValidator = useCallback(
    (validator: (session: MeetingSessionOvenMediaReceiver) => boolean) => {
      audioValidatorsRef.current.push(validator);
      updateAudioValidators();
      return {
        flush: () => {
          removeAudioValidator(validator);
        }
      }
    },
    [updateAudioValidators, removeAudioValidator]
  );



  const state: PedagogyContextState = {
    ...expertState,
    // -------
    // pedagogy
    // -------
    // Containers
    containers,
    setContainers,
    pedagogyContainers,
    setPedagogyContainers,
    scheduleDestroyContainer,
    unscheduleDestroyContainer,
    containersExceptPedagogy: containers.filter((c1) => !pedagogyContainers.find((c2) => c1 === c2)) ?? [],
    // Actions
    actions,
    setActions,
    confirmableAction: finalizableAction,
    setConfirmableAction: setFinalizableAction,
    // Meetings
    pedagogyMeetings: pedagogyMeetings.filter(Boolean) as Meeting[],
    // Expert sessions
    disabledExpertMediaSessionIds,
    setDisabledExpertMediaSessionIds,
    enableExpertMediaSessionId: (id) => {
      const ids = Array.isArray(id) ? id : [id];
      const newValues = new Set<string>(disabledExpertMediaSessionIds);
      ids.forEach((id) => newValues.delete(id));
      if (newValues.size !== disabledExpertMediaSessionIds.length) {
        // array change
        setDisabledExpertMediaSessionIds(Array.from(newValues));
      }
    },
    disableExpertMediaSessionId: (id) => {
      const ids = Array.isArray(id) ? id : [id];
      const newValues = new Set<string>(disabledExpertMediaSessionIds);
      ids.forEach((id) => newValues.add(id));
      if (newValues.size !== disabledExpertMediaSessionIds.length) {
        // array change
        setDisabledExpertMediaSessionIds(Array.from(newValues));
      }
    },
    // Audio
    audioValidator: audioValidatorCallback.callback,
    addAudioValidator,
    removeAudioValidator,
    // Layout
    layout,
    setLayout,
    // Remote controller
    displayRemoteController,
    setDisplayRemoteController,
  };
  return <PedagogyContext.Provider value={state}>{children}</PedagogyContext.Provider>;
};
